import { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ReactComponent as Frame1Image } from './Frame1.svg'
import { ReactComponent as Frame1WholesaleImage } from './Frame1_wholesale.svg'
import { ReactComponent as Frame2Image } from './Frame2.svg'
import { ReactComponent as Frame3Image } from './Frame3.svg'
import Footer from "../../components/Footer";
import IllustrationStep from './IllustrationStep'
import HowHeardStep from './HowHeardStep'
import AmazonLevelStep from './AmazonLevelStep'
import HurdleStep from './HurdleStep'
import GoalsStep from './GoalsStep'
import EdgeStep from './EdgeStep'
import IntroductionStep from './IntroductionStep'
import { useUpdateFilterMutation } from '../../services/onboarding'
import { getStripeCheckoutUrlAPI } from '../../lib/api/api'
import { useTheme } from '../../context/themeContext'
import { ReactComponent as DealsIcon } from './HurdleStep/icon-deals.svg'
import { ReactComponent as SystemIcon } from './HurdleStep/icon-system.svg'
import { ReactComponent as TimeIcon } from './HurdleStep/icon-time.svg'
import { ReactComponent as UnsureIcon } from './HurdleStep/icon-unsure.svg'


export default function Onboarding() {
  let [at, setAt] = useState(1)
  let [data, setData] = useState({})
  let [searchParams] = useSearchParams()
  let plan = getPlanFromSearchParams(searchParams)
  let { theme } = useTheme()

  useEffect(() => {
    const root = document.documentElement
    root.classList.add('dark')
    return () => {
      if (theme !== 'dark') {
        root.classList.remove('dark')
      }
    }
  }, [theme])

  let { mutateAsync: postData } = useUpdateFilterMutation()
  let isScanproPlan = useMemo(
    () => ['scanpro', 'scanproplus', 'scanpro_yearly', 'scanproplus_yearly', 'scanproplus_trial'].includes(plan),
    [plan]
  )
  let hurdleOptions = useMemo(() => {
    if (!isScanproPlan) return undefined
    return [
      { icon: SystemIcon, value: 'system', label: 'Mir fehlt ein klares System' },
      { icon: TimeIcon, value: 'analyse', label: 'Ich verliere Zeit beim analysieren von Deals' },
      { icon: UnsureIcon, value: 'unsicher', label: 'Ich bin mir unsicher bei Angeboten' },
      { icon: DealsIcon, value: 'grosshaendler', label: 'Mir fehlen gute Großhändler' },
    ]
  }, [isScanproPlan])

  let onInputStepSubmit = useCallback((stepData) => {
    setData((prev) => ({ ...prev, ...stepData }))
    setAt((prev) => prev + 1)
  }, [])

  let FrameOneIllustration = isScanproPlan ? Frame1WholesaleImage : Frame1Image

  let steps = useMemo(() => [
    <IllustrationStep
      key='illustration'
      illustration={FrameOneIllustration}
      title={<>
        {isScanproPlan ? (
          <>
            Der #1 Großhandelsscanner<br />
            für mehr Erfolg auf Amazon
          </>
        ) : (
          <>
            Dein All in One Tool für mehr<br />
            Erfolg mit Online Arbitrage
          </>
        )}
      </>}
      description={<>
        {isScanproPlan ? (
          <>
            Verabschiede dich von Scannern die nur Daten visualisieren,<br />
            hin zu einer Welt aus effizienz im Sourcing deiner Listen durch clevere<br />
            Nutzung von Daten und künstlicher Intelligenz
          </>
        ) : (
          <>
            Finde hunderte profitable Produkte,  überwache<br />
            Wettbewerber und stelle tausende Preiswecker -<br />
            Messbare Erfolge aus einer Hand.
          </>
        )}
      </>}
      step={1}
      onStepChange={setAt}
      totalSteps={3}
    />,
    <IllustrationStep
      key='illustration'
      illustration={Frame2Image}
      title={<>
        In 3 Schritten zum ersten <em className='text-primary'>Profit</em><br />
        Spare Zeit, Aufwand und Geld
      </>}
      description={<>
        Finde mehr Produkte, durch deinen ganz<br />
        eigenen Sourcing Prozess - powered by Edge.
      </>}
      step={2}
      onStepChange={setAt}
      totalSteps={3}
    />,
    <IllustrationStep
      key='illustration'
      illustration={Frame3Image}
      title={<>
        Sei der nächste Seller der durch<br />
        Edge aufs nächste Level skaliert ツ
      </>}
      description={<>
        {isScanproPlan ? (
          <>
            Du bist nicht der Erste Seller aber auch nicht der letzte<br />
            der den Weg zu mehr Gewinn durch clevere Software geht
          </>
        ) : (
          <>
            Connect with your favorite creators in the space<br />
            and get access to highly sought after deals
          </>
        )}
      </>}
      step={3}
      onStepChange={setAt}
      totalSteps={3}
    />,
    <IntroductionStep onSubmit={onInputStepSubmit} />,
    <HowHeardStep onSubmit={onInputStepSubmit} />,
    <AmazonLevelStep onSubmit={onInputStepSubmit} />,
    <HurdleStep options={hurdleOptions} onSubmit={onInputStepSubmit} />,
    <GoalsStep onSubmit={onInputStepSubmit} />,
    <EdgeStep onSubmit={async () => {
      await postData({ ...data, plan })
      const { url } = await getStripeCheckoutUrlAPI(plan)
      if (!url) throw new Error('Stripe checkout response missing url')
      window.location.href = url
    }} />
  ], [FrameOneIllustration, data, hurdleOptions, isScanproPlan, onInputStepSubmit, plan, postData])

  return (
    <div className='flex flex-col w-full h-full items-center justify-between overflow-auto select-none'>
      {steps[at - 1]}
      <Footer className='mt-6 pl-0' />
    </div>
  )
}

function getPlanFromSearchParams(searchParams) {
  if (searchParams.has('lite')) return 'lite_trial'
  if (searchParams.has('pro')) return 'pro_trial'
  if (searchParams.has('proplus')) return 'proplus_trial'
  if (searchParams.has('lite_yearly')) return 'lite_yearly'
  if (searchParams.has('pro_yearly')) return 'pro_yearly'
  if (searchParams.has('proplus_yearly')) return 'proplus_yearly'
  if (searchParams.has('scanpro')) return 'scanpro'
  if (searchParams.has('scanproplus')) return 'scanproplus'
  if (searchParams.has('scanpro_yearly')) return 'scanpro_yearly'
  if (searchParams.has('scanproplus_yearly')) return 'scanproplus_yearly'
  
  return 'pro_trial'
}
